import React from "react";
import { Button } from "../../../components/ui";

interface TrialUpgradeCardProps {
  /** What the product is called — "Naurist Pro". */
  productName: string;
  /** Opens the price list, which is where a cycle is actually chosen. */
  onUpgrade: () => void;
}

/**
 * The one thing left to do about a trial: start paying for it.
 *
 * It takes the place `RefundRequestCard` and `CancelPlanCard` hold for a
 * subscriber — the foot of Plan details, where what can still be done about the
 * money lives. For a trial there is nothing to refund and nothing to cancel; the
 * fortnight ends on its own, and the only action is the one that keeps the
 * workspace open past it.
 *
 * It buys nothing on its own. There are two cycles and this card names neither,
 * so it opens the price list and the choice is made there — a button here that
 * went straight to a checkout would be picking a term on somebody's behalf.
 */
const TrialUpgradeCard: React.FC<TrialUpgradeCardProps> = ({
  productName,
  onUpgrade,
}) => (
  <section
    aria-labelledby="switch-to-paid-heading"
    className="card-surface border border-line bg-white"
  >
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 3xl:px-6 3xl:py-5">
      <div className="min-w-0">
        <h2
          id="switch-to-paid-heading"
          className="text-[15px] 3xl:text-[16px] font-medium text-ink"
        >
          Switch to paid
        </h2>
        {/* What it buys, said as continuity rather than as a deadline: the
            countdown at the top of the screen has already been read, and
            repeating it here would be pressing. */}
        <p className="mt-1 text-[13px] 3xl:text-[14px] text-ink-muted">
          Continue enjoying {productName} without any interruption after your
          trial ends.
        </p>
      </div>

      {/* Full width on a phone, the way `CancelPlanCard`'s is. */}
      <Button
        variant="primary"
        size="sm"
        onClick={onUpgrade}
        className="w-full md:w-auto"
      >
        Upgrade
      </Button>
    </div>
  </section>
);

export default TrialUpgradeCard;
